import { createFileRoute } from '@tanstack/react-router'
import { Phone } from 'lucide-react'
import { PageHeader } from '@/components/layout/page-header'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader } from '@/components/ui/card'

export const Route = createFileRoute('/_app/integrations')({
  head: () => ({ meta: [{ title: 'Integrations - MyJunea' }] }),
  component: IntegrationsPage,
})

interface Integration {
  name: string
  description: string
  logo: string
  logoBg?: string
  status: 'active' | 'coming-soon'
}

const integrations: Integration[] = [
  {
    name: 'Sportivity',
    description:
      'Let members of your sports club or gym pay directly from their Sportivity account. Sportivity is an all-in-one club management platform used by over 435,000 members to manage memberships, class bookings and payments.',
    logo: '/images/integrations/sportivity.svg',
    logoBg: 'bg-[#3A9AA0]',
    status: 'active',
  },
  {
    name: 'Leat',
    description:
      "Reward customers through Leat's unified loyalty platform. Leat lets you offer points, stamp cards, gift cards and membership perks — all redeemable at your vending machines.",
    logo: '/images/integrations/leat.png',
    status: 'coming-soon',
  },
  {
    name: 'Authic',
    description:
      'Let customers pay with their Authic loyalty balance. Authic is a white-label loyalty platform that helps businesses in hospitality, wellness, retail and sports build branded reward programs — now usable at your vending machines too.',
    logo: '/images/integrations/authic.png',
    status: 'coming-soon',
  },
]

function IntegrationCard({ integration }: { integration: Integration }): React.ReactElement {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div
            className={`flex h-10 items-center rounded-lg ${integration.logoBg !== undefined ? `${integration.logoBg} px-3 py-2` : ''}`}
          >
            <img
              src={integration.logo}
              alt={`${integration.name} logo`}
              className="h-6 object-contain"
            />
          </div>
          {integration.status === 'coming-soon' && (
            <Badge variant="secondary" className="shrink-0">
              Coming soon
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-muted-foreground text-sm">{integration.description}</p>
      </CardContent>
    </Card>
  )
}

function IntegrationsPage(): React.ReactElement {
  return (
    <div className="space-y-6">
      <PageHeader
        title="Integrations"
        description="These integrations let customers pay at your vending machines using their existing membership or loyalty balance — no card or cash needed."
      />
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {integrations.map((integration) => (
          <IntegrationCard key={integration.name} integration={integration} />
        ))}
      </div>
      <div className="flex justify-center pt-4">
        <Button size="lg" asChild>
          <a href="tel:+31851305719">
            <Phone />
            Call us for more info
          </a>
        </Button>
      </div>
    </div>
  )
}
